import { BundleDiscountSystem } from "src/types/bundle-discounts/BundleDiscountSystem";
import { BundleDiscountType } from "src/types/bundle-discounts/BundleDiscountType";
import { cloneDeep, sortBy } from "lodash";

/**
 * Bundle Configuration Manager
 * Provides utilities to manage dynamic bundle configurations
 */
export class BundleConfigManager {
  private bundles: BundleDiscountSystem[];

  constructor(bundles: BundleDiscountSystem[]) {
    this.bundles = cloneDeep(bundles);
  }

  /**
   * Get all active bundles sorted by priority and minimum items
   */
  getActiveBundles(): BundleDiscountSystem[] {
    const activeBundles = this.bundles.filter((bundle) => bundle.active !== false);

    // Sort by priority (higher first), then by minimumItems (higher first)
    return sortBy(activeBundles, [(bundle) => -(bundle.priority || 0), (bundle) => -bundle.minimumItems]);
  }

  /**
   * Get qualifying bundles for a given quantity
   */
  getQualifyingBundles(totalQuantity: number): BundleDiscountSystem[] {
    const activeBundles = this.getActiveBundles();
    return activeBundles.filter((bundle) => totalQuantity >= bundle.minimumItems);
  }

  /**
   * Get the best bundle for a given quantity (highest priority/value)
   */
  getBestBundle(totalQuantity: number): BundleDiscountSystem | null {
    const qualifyingBundles = this.getQualifyingBundles(totalQuantity);
    return qualifyingBundles[0] || null;
  }

  /**
   * Activate a bundle by type
   */
  activateBundle(bundleType: BundleDiscountType): void {
    const bundle = this.bundles.find((b) => b.type === bundleType);
    if (bundle) {
      bundle.active = true;
    }
  }

  /**
   * Deactivate a bundle by type
   */
  deactivateBundle(bundleType: BundleDiscountType): void {
    const bundle = this.bundles.find((b) => b.type === bundleType);
    if (bundle) {
      bundle.active = false;
    }
  }

  /**
   * Toggle bundle activation
   */
  toggleBundle(bundleType: BundleDiscountType): void {
    const bundle = this.bundles.find((b) => b.type === bundleType);
    if (bundle) {
      bundle.active = !bundle.active;
    }
  }

  /**
   * Set multiple bundles active/inactive
   */
  setBundlesActive(bundleTypes: BundleDiscountType[], active: boolean): void {
    bundleTypes.forEach((bundleType) => {
      const bundle = this.bundles.find((b) => b.type === bundleType);
      if (bundle) {
        bundle.active = active;
      }
    });
  }

  /**
   * Get all available bundle types
   */
  getAllBundleTypes(): BundleDiscountType[] {
    return this.bundles.map((bundle) => bundle.type);
  }

  /**
   * Get bundle by type
   */
  getBundleByType(bundleType: BundleDiscountType): BundleDiscountSystem | null {
    return this.bundles.find((bundle) => bundle.type === bundleType) || null;
  }

  /**
   * Add a new bundle configuration
   */
  addBundle(bundle: BundleDiscountSystem): void {
    this.bundles.push(cloneDeep(bundle));
  }

  /**
   * Remove a bundle configuration
   */
  removeBundle(bundleType: BundleDiscountType): void {
    this.bundles = this.bundles.filter((bundle) => bundle.type !== bundleType);
  }

  /**
   * Update an existing bundle
   */
  updateBundle(bundleType: BundleDiscountType, updates: Partial<BundleDiscountSystem>): void {
    const bundle = this.bundles.find((b) => b.type === bundleType);
    if (bundle) {
      Object.assign(bundle, updates);
    }
  }

  /**
   * Get bundle summary for debugging/display
   */
  getBundleSummary(): Array<{
    type: BundleDiscountType;
    active: boolean;
    description?: string;
    minimumItems: number;
    priority?: number;
  }> {
    return this.bundles.map((bundle) => ({
      type: bundle.type,
      active: bundle.active !== false,
      description: bundle.description,
      minimumItems: bundle.minimumItems,
      priority: bundle.priority,
    }));
  }
}
